/**
 * Memory management utilities for mobile devices
 */

// Track active blob URLs to ensure cleanup
const activeBlobUrls = new Set<string>();

/**
 * Create a blob URL and track it for cleanup
 */
export const createTrackedBlobUrl = (blob: Blob): string => {
  const url = URL.createObjectURL(blob);
  activeBlobUrls.add(url);
  return url;
};

/**
 * Revoke a tracked blob URL
 */
export const revokeTrackedBlobUrl = (url: string) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
    activeBlobUrls.delete(url);
  }
};

/**
 * Revoke all tracked blob URLs
 */
export const revokeAllTrackedBlobUrls = () => {
  activeBlobUrls.forEach(url => {
    URL.revokeObjectURL(url);
  });
  activeBlobUrls.clear();
};

/**
 * Force garbage collection hint by clearing references
 */
export const forceGarbageCollection = async (delayMs: number = 100) => {
  await new Promise(resolve => setTimeout(resolve, delayMs));
};

/**
 * Unload image from DOM to free memory
 */
export const unloadImage = (imgElement: HTMLImageElement | null) => {
  if (!imgElement) return;
  
  imgElement.src = '';
  imgElement.srcset = '';
  imgElement.removeAttribute('src');
  imgElement.removeAttribute('srcset');
};

/**
 * Clear all images in a container
 */
export const clearImagesInContainer = (container: HTMLElement) => {
  const images = container.querySelectorAll('img');
  images.forEach(img => unloadImage(img));
};

/**
 * Aggressive memory cleanup - use before new operations
 */
export const aggressiveCleanup = async () => {
  // Clear all tracked blobs
  revokeAllTrackedBlobUrls();
  
  // Force multiple GC hints
  await forceGarbageCollection(50);
  await forceGarbageCollection(50);
  
  // Clear browser caches if possible
  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    } catch (e) {
      console.warn('Could not clear caches:', e);
    }
  }
};
